import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { BillingAddress } from '@/types';
import { getCountries } from '@/utils/BillingInformation';
import { replaceLocationBasedTerms } from '@/utils/replaceLocationBasedTerms';
import { InputField, SelectField, Typography } from '@prenuvo/halo-web';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

export const BillingInformation = () => {
  const { add, locationId, billingAddress, paymentMethodType, errorsPayment, removeError } =
    usePrenuvoStore(
      useShallow((state) => ({
        add: state.payment.methods.add,
        locationId: state.scan.locationId,
        billingAddress: state.payment.billingAddress,
        paymentMethodType: state.payment.paymentMethodType,
        errorsPayment: state.payment.errors,
        removeError: state.payment.methods.removeError,
      })),
    );

  const [address, setAddress] = useState<BillingAddress>(billingAddress);

  const handleAddressChange = (addressChange: { [key: string]: string }) => {
    const key = Object.keys(addressChange)[0];
    removeError(key);
    setAddress({ ...address, ...addressChange });
    add({ billingAddress: { ...address, ...addressChange } });
  };

  const handleCountryChange = (value: string) => {
    handleAddressChange({ country: value });
  };

  const countryOptions = getCountries();

  return (
    <div className="flex flex-col gap-4 border-b border-neutral-200 pb-6">
      <div className="gap-2">
        <Typography variant="h4" as="h4">
          Billing Information
        </Typography>
        <Typography variant="paragraph-md" as="p">
          {replaceLocationBasedTerms(
            'Please enter the following details for the person that will be getting scanned',
            locationId,
          )}
          .
        </Typography>
      </div>
      <SelectField
        onChange={(value: string | undefined) => value && handleCountryChange(value)}
        options={countryOptions}
        value={billingAddress.country}
        name="country"
        data-testid="country"
        placeHolder={{ children: 'Country' }}
        isInvalid={!!errorsPayment?.country}
        caption={{
          translationKey: errorsPayment?.country && 'Billing address country is required',
        }}
      />
      {paymentMethodType === 'affirm' && (
        <>
          <InputField
            size="lg"
            type="text"
            variant="outlined"
            name="state"
            label={{ children: 'State' }}
            placeholder={{ children: 'Enter your state' }}
            onChange={(e) => handleAddressChange({ state: e.target.value })}
            value={billingAddress.state}
            isInvalid={!!errorsPayment?.state}
            caption={{
              translationKey: errorsPayment?.state,
            }}
            data-testid="state"
          />
          <InputField
            size="lg"
            type="text"
            variant="outlined"
            name="city"
            label={{ children: 'City' }}
            placeholder={{ children: 'Enter your city' }}
            onChange={(e) => handleAddressChange({ city: e.target.value })}
            value={billingAddress.city}
            isInvalid={!!errorsPayment?.city}
            caption={{
              translationKey: errorsPayment?.city,
            }}
            data-testid="city"
          />
          <InputField
            size="lg"
            type="text"
            variant="outlined"
            name="address"
            label={{ children: 'Address' }}
            placeholder={{ children: 'Enter your address' }}
            onChange={(e) => handleAddressChange({ address1: e.target.value })}
            value={billingAddress.address1}
            isInvalid={!!errorsPayment?.address1}
            caption={{
              translationKey: errorsPayment?.address1,
            }}
            data-testid="address"
          />
        </>
      )}
      <InputField
        size="lg"
        type="text"
        variant="outlined"
        name="postalCode"
        label={{ children: 'Postal Code' }}
        placeholder={{ children: 'Enter your postal code' }}
        onChange={(e) => handleAddressChange({ postalCode: e.target.value })}
        isInvalid={!!errorsPayment?.postalCode}
        caption={{
          translationKey: errorsPayment?.postalCode,
        }}
        value={billingAddress.postalCode}
        data-testid="postal-code"
      />
    </div>
  );
};
